import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Sparkles, ArrowRight, X } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { fetchUsage, dismissFirstRoutineNudge } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { track } from "../lib/analytics";

/**
 * FirstRoutineNudge — one-time activation card.
 *
 * Shown to free-tier users who signed up ≥24h ago and haven't yet
 * generated their first routine. Backend (`/api/me/usage`) decides
 * eligibility via `should_show_first_routine_nudge`; this component
 * just renders the card and handles dismiss / CTA click.
 *
 * Lifecycle
 * ─────────
 *   • Mount → call fetchUsage. If `should_show_first_routine_nudge`
 *     is true, render the card + fire `first_routine_nudge_shown`.
 *   • CTA click → fire `first_routine_nudge_clicked`, navigate to
 *     `/capture`. We don't dismiss here — if the user backs out
 *     without doing a routine, we want to re-show the nudge later.
 *   • Dismiss (X) → fire `first_routine_nudge_dismissed`, POST the
 *     dismiss endpoint, hide forever.
 *
 * Privacy: completely silent for guests, Pro users, and stealth users
 * who happened to also be on free tier — the backend already handles
 * the eligibility check. We don't even fire a tracking event until
 * the card actually shows.
 */

const FirstRoutineNudge = ({ variant = "card" }) => {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [visible, setVisible] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [trackedOnce, setTrackedOnce] = useState(false);

  useEffect(() => {
    if (authLoading) return;
    if (!user) return; // never for unauthed
    let cancelled = false;
    (async () => {
      try {
        const usage = await fetchUsage();
        if (cancelled) return;
        if (usage?.should_show_first_routine_nudge) {
          setVisible(true);
        }
      } catch {
        /* silent — nudge stays hidden on usage fetch failure */
      }
    })();
    return () => { cancelled = true; };
  }, [authLoading, user]);

  useEffect(() => {
    if (visible && !trackedOnce) {
      track("first_routine_nudge_shown", { variant });
      setTrackedOnce(true);
    }
  }, [visible, trackedOnce, variant]);

  const handleCta = () => {
    track("first_routine_nudge_clicked", { variant });
    navigate("/capture");
  };

  const handleDismiss = async () => {
    track("first_routine_nudge_dismissed", { variant });
    setDismissed(true);
    try {
      await dismissFirstRoutineNudge();
    } catch {
      /* idempotent server-side; the user's local hide is enough */
    }
  };

  if (!visible || dismissed) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, y: -8 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -8 }}
        transition={{ duration: 0.35, ease: "easeOut" }}
        className="relative bg-gradient-to-br from-[#FBF4F2] via-[#F4E5E1] to-[#EFD8D2] border border-[#E4D2DC] rounded-2xl p-5 pr-12 overflow-hidden shadow-[0_12px_32px_-20px_rgba(155,106,108,0.45)]"
        data-testid="first-routine-nudge"
      >
        <div className="absolute -top-10 -right-10 h-32 w-32 rounded-full bg-[#9B6A6C]/15 blur-3xl pointer-events-none" />

        <div className="flex items-start gap-3 relative z-10">
          <div className="shrink-0 h-10 w-10 rounded-full bg-white/85 border border-[#E4D2DC] flex items-center justify-center">
            <Sparkles className="h-5 w-5 text-[#9B6A6C]" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[11px] uppercase tracking-[0.22em] text-[#9B6A6C] mb-1">
              Trouvé is waiting
            </div>
            <h4 className="text-[#2C2123] font-medium leading-snug mb-1 text-base">
              Your first routine is one selfie away
              <span aria-hidden> 👀</span>
            </h4>
            <p
              className="text-sm text-[#5C4B51] leading-relaxed mb-3"
              data-testid="first-routine-nudge-body"
            >
              You signed up — and we're so glad you did. Take a single selfie
              and we'll generate a coached routine tuned to your features in under a minute.
            </p>
            <div className="flex flex-wrap items-center gap-3">
              <button
                type="button"
                onClick={handleCta}
                data-testid="first-routine-nudge-cta"
                className="inline-flex items-center gap-1.5 bg-[#9B6A6C] hover:bg-[#5D4050] text-white px-5 py-2 rounded-full font-medium text-sm transition-colors"
              >
                Start my first routine
                <ArrowRight className="h-3.5 w-3.5" />
              </button>
              <button
                type="button"
                onClick={handleDismiss}
                data-testid="first-routine-nudge-later"
                className="text-xs text-[#9B6A6C] hover:text-[#5D4050] underline-offset-4 hover:underline transition-colors"
              >
                Maybe later
              </button>
            </div>
          </div>
        </div>

        <button
          type="button"
          onClick={handleDismiss}
          aria-label="Dismiss"
          title="Dismiss"
          className="absolute top-3 right-3 h-7 w-7 rounded-full text-[#9B6A6C] hover:bg-white/70 hover:text-[#5D4050] transition-colors flex items-center justify-center z-20"
          data-testid="first-routine-nudge-dismiss"
        >
          <X className="h-4 w-4" />
        </button>
      </motion.div>
    </AnimatePresence>
  );
};

export default FirstRoutineNudge;
